import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Target, Loader2 } from "lucide-react";
import { getExecutiveActions, track } from "../lib/api";

/**
 * ExecutiveActionsModal — The "killer demo question":
 *   "If you ran my business, what 3 things would you do this week?"
 *
 * LLM gets the cherries + headline and synthesizes 3 actionable steps with
 * priority, why, expected impact, and owner hint.
 */

const PRIORITY_STYLES = {
  1: { ring: "ring-red-500/40", chip: "bg-red-500/15 text-red-300", label: "P1 · דחוף" },
  2: { ring: "ring-amber-500/40", chip: "bg-amber-500/15 text-amber-300", label: "P2 · השבוע" },
  3: { ring: "ring-cyan-500/40", chip: "bg-cyan-500/15 text-cyan-300", label: "P3 · בקרוב" },
};

export default function ExecutiveActionsModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [actions, setActions] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setActions(null);
    track("executive_actions_open", "/", {});
    getExecutiveActions()
      .then((r) => setActions(r.data?.actions || []))
      .catch((e) => setError(e?.response?.data?.detail || "שגיאה בקבלת התוכנית"))
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="exec-actions-modal"
        >
          <motion.div
            initial={{ scale: 0.95, y: 10 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 10 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[88vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-card border-b border-border px-5 py-4 flex items-start justify-between gap-3 z-10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-mono mb-1 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" />
                  Pulse AI · יועץ אישי
                </div>
                <h2 className="text-base sm:text-lg font-medium leading-snug">
                  אם הייתי מנהל את העסק שלך — מה הייתי עושה השבוע?
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-zinc-500 hover:text-foreground p-1 -m-1 shrink-0"
                data-testid="exec-actions-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-5">
              {loading && (
                <LoadingState />
              )}
              {error && !loading && (
                <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded p-3">
                  {error}
                </div>
              )}
              {!loading && actions && actions.length > 0 && (
                <div className="space-y-3" data-testid="exec-actions-list">
                  {actions.map((a, i) => (
                    <ActionRow key={i} action={a} index={i} />
                  ))}
                  <div className="text-[11px] text-zinc-500 leading-relaxed pt-3 border-t border-border/40 mt-2">
                    💡 Pulse AI סינתז את הפעולות האלה מהדובדבנים שזיהינו בנתוני ה-Priority שלך. כל פעולה מבוססת על מספר אמיתי.
                  </div>
                </div>
              )}
              {!loading && actions && actions.length === 0 && !error && (
                <div className="text-sm text-zinc-400 text-center py-8">
                  לא נמצאו פעולות. נסה שוב בעוד רגע.
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ActionRow({ action, index }) {
  const style = PRIORITY_STYLES[action.priority] || PRIORITY_STYLES[index + 1] || PRIORITY_STYLES[3];
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.08 }}
      className={`bg-[#0A0A0E]/60 border border-border rounded-md p-4 ring-1 ${style.ring}`}
      data-testid={`exec-action-${index}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-mono ${style.chip}`}>
            {style.label}
          </span>
          {action.owner_hint && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-zinc-400 bg-zinc-800/50">
              {action.owner_hint}
            </span>
          )}
        </div>
        <Target className="w-4 h-4 text-zinc-600 shrink-0 mt-0.5" strokeWidth={1.5} />
      </div>
      <h3 className="text-base font-semibold text-foreground leading-snug mb-2">
        {action.title}
      </h3>
      {action.why && (
        <div className="text-[12px] text-zinc-400 leading-relaxed mb-2">
          <span className="text-zinc-500 font-medium">למה: </span>
          {action.why}
        </div>
      )}
      {action.impact && (
        <div className="text-[12px] text-primary leading-relaxed">
          <span className="text-zinc-500 font-medium">השפעה צפויה: </span>
          {action.impact}
        </div>
      )}
    </motion.div>
  );
}

const LOADING_STAGES = [
  "סורק את הדובדבנים שזוהו...",
  "מצליב עם המגמות החודשיות...",
  "בונה תוכנית פעולה לשבוע...",
  "מסנן ל-3 הפעולות שמקסימליות impact...",
];

function LoadingState() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % LOADING_STAGES.length), 2500);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="flex flex-col items-center py-10 text-center" data-testid="exec-actions-loading">
      <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" strokeWidth={1.5} />
      <div className="text-sm text-foreground mb-1">Pulse AI מנתח את העסק שלך</div>
      <div className="text-xs text-zinc-500 max-w-xs leading-relaxed">
        {LOADING_STAGES[idx]}
      </div>
      <div className="text-[11px] text-zinc-600 mt-4">זה לוקח 15-30 שניות — אל תסגור 🔍</div>
    </div>
  );
}
